import { cloneDeep, omit } from 'lodash';
import { v4 as uuid } from 'uuid';
import { type IFramework } from '@/framework';
import { createTextContentsByArtText, getLayerFontFamilyIds } from '@/layers/text-layer/utils';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import {
    MaterialIdsType,
    TextAlignType,
    TextWritingType,
    type EditingRange,
    type ILayerProps,
    type ITextContent,
    type ITextLayerProps,
} from '@/version-types';
import { TEXT_EDIT_EVENTS } from '../constants';
import { TextEditDependency } from '../dependency';
import { getTextEditState } from '../states/text-edit-state';
import { AbilityDependency, ClipboardData } from '../types';
import { insertPlainText } from './dom';

export class TextLayerClipboard {
    private constructor() {
        throw new Error('TextLayerClipboard is static class');
    }

    private static framework: IFramework | null;

    /**
     * 初始化 framework
     * 为避免各方法参数都传入 framework，各方法需要在调用 init 后再进行链式调用
     *
     * @param framework - 框架实例
     */
    public static init(framework: IFramework) {
        this.framework = framework;
        return this;
    }

    private static warnNotInitialized() {
        console.warn('TextLayerClipboard has not been initialized.');
    }

    private static calcLayout(prevLayer: ILayerProps, nextLayer: ILayerProps) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (prevLayerData.shapeExtension) return nextLayer;

        const ret: ILayerProps = cloneDeep(nextLayer);

        const { writingMode } = prevLayerData;
        const fixedDirectionSize = nextLayer.__fixedDirectionSize;

        const measureOptions = {
            keepConstantWidth:
                writingMode === TextWritingType.HORIZONTAL_TB &&
                (fixedDirectionSize ||
                    checkIsHorizontalMultiLine(nextLayer.height, prevLayerData.fontSize, prevLayerData.lineSpacing)),
            keepConstantHeight: false,
        };

        let textSize = TextMeasurer.measure(nextLayer, measureOptions);

        const stageIdx = this.framework!.stage.getActiveStageIndex();
        const stageWidth = this.framework!.getSizeByIndex(stageIdx).width;

        const unsetFixedDirectionSize = typeof fixedDirectionSize === 'undefined';
        const willOverflow = textSize.width + nextLayer.left > stageWidth;

        if (willOverflow && unsetFixedDirectionSize) {
            textSize = TextMeasurer.measure(
                { ...nextLayer, width: stageWidth },
                { ...measureOptions, keepConstantWidth: true },
            );
            ret.__fixedDirectionSize = true;
        }

        // 设置文本图层尺寸
        const layerWidth = nextLayer.width;
        const nextLayerWidth = Math.max(textSize.width, layerWidth);
        const nextLayerHeight = textSize.height;
        ret.width = nextLayerWidth;
        ret.height = nextLayerHeight;

        // 设置文本图层定位
        const { textAlign } = prevLayerData;
        if (textAlign === TextAlignType.CENTER) {
            ret.left = prevLayer.left + (layerWidth - nextLayerWidth) / 2;
        } else if (textAlign === TextAlignType.RIGHT) {
            ret.left = prevLayer.left + (layerWidth - nextLayerWidth);
        }

        return ret;
    }

    private static insertArtTextCharacters(layer: ILayerProps, data: ClipboardData, editingRange: EditingRange) {
        const { startIndex, endIndex, isCollapsed } = editingRange;
        const { text } = data;

        // --- case 1: 若选区是折叠的，直接插入文本 ---
        // 后续的更新直接由 input 事件触发 onUpdate 更新即可
        if (isCollapsed) return insertPlainText(text);

        // --- case 2: 若选区不是折叠的，需要手动构造花字的单字 textContent 集合插入 ---

        const prevLayerData = layer.data as ITextLayerProps;
        const { textContents: prevTextContents = [], advancedTextMaterial } = prevLayerData;

        if (!advancedTextMaterial) return;

        // 确认 startIndex 和 endIndex 是否在有效范围内
        if (
            startIndex < 0 ||
            startIndex >= prevTextContents.length ||
            endIndex < 0 ||
            endIndex >= prevTextContents.length ||
            startIndex > endIndex
        )
            return;

        const insertedTextContents = createTextContentsByArtText(text, prevLayerData);
        const nextTextContents: ITextContent[] = [
            ...prevTextContents.slice(0, startIndex),
            ...insertedTextContents,
            ...prevTextContents.slice(endIndex),
        ];
        const nextText = nextTextContents.map((item) => item.text).join('');
        const nextLayerData: ITextLayerProps = { ...prevLayerData, text: nextText, textContents: nextTextContents };
        let nextLayer: ILayerProps = { ...layer, data: nextLayerData };
        nextLayer = this.calcLayout(layer, nextLayer);
        const nextEditingRange: EditingRange = {
            ...editingRange,
            startIndex,
            endIndex: startIndex + insertedTextContents.length,
            isCollapsed: true,
        };

        this.framework!.events.emit(TEXT_EDIT_EVENTS.ALLOW_UPDATE_MANUAL);

        const prevLayer = cloneDeep(layer);

        this.framework!.stage.addOrUpdate(nextLayer);
        this.framework!.operate.setActiveLayer(nextLayer.id);
        this.framework!.history.write(omit(prevLayer, '__isOnTextEditing'), omit(nextLayer, '__isOnTextEditing'));

        getTextEditState(this.framework!.identity).setEditingRange(nextEditingRange);
    }

    public static paste(layer: ILayerProps, data: ClipboardData, editingRange: EditingRange) {
        if (!this.framework) {
            this.warnNotInitialized();
            return;
        }

        const { text } = data;
        if (!text) return;

        const prevLayerData = layer.data as ITextLayerProps;

        // --- case 0: 若文本图层应用了花字效果，则走单独的花字插入逻辑 ---
        const { advancedTextMaterial } = prevLayerData;
        if (advancedTextMaterial) {
            return this.insertArtTextCharacters(layer, data, editingRange);
        }

        // --- case 1: 若选区是折叠的，直接插入文本 ---
        // 后续的更新直接由 input 事件触发 onUpdate 更新即可
        if (editingRange.isCollapsed) {
            return insertPlainText(text);
        }

        // --- case 2: 若选区不是折叠的，需要将新插入的文本当做一段分段文本处理 ---
        // 目的是防止将文本插入到 span 外部，造成后续分段数据无法获取（尤其是选中到结尾时容易发生该情况）
        // 将要插入的文字认定为一段分段文本，样式继承自图层一级属性

        const prevTextContents = prevLayerData.textContents || [];

        const { startIndex, startOffset, endIndex, endOffset, isEnd } = editingRange;

        let nextText = prevLayerData.text;
        let nextTextContents: ITextContent[] = cloneDeep(prevTextContents);
        let nextEditingRange: EditingRange = { ...editingRange, isCollapsed: true };

        // case 2.1 若选中的是整个选区，即 startIndex === endIndex
        // 2.1.1 若图层没有分段文本，此时选区不会跨越 span, 即不存在将文本插入到 span 外部的情况
        // 2.1.2 若图层有分段文本，更新整段属性
        if (startIndex === endIndex) {
            if (prevTextContents.length === 0) return insertPlainText(text);

            // 确保 startIndex 在有效范围内
            if (startIndex < 0 || startIndex >= prevTextContents.length) return;

            nextTextContents = nextTextContents.map((item, idx) => {
                if (idx === startIndex) {
                    const scopeText = item.text.slice(0, startOffset) + text + item.text.slice(endOffset);
                    return { ...item, text: scopeText };
                }
                return item;
            });
            nextText = nextTextContents.map((item) => item.text).join('');

            nextEditingRange = {
                ...nextEditingRange,
                endOffset: startOffset + text.length,
            };
        } else {
            // case 2.2 若选中的是夸分段文本，即 startIndex !== endIndex

            // 确保 startIndex 和 endIndex 在有效范围内
            if (
                startIndex < 0 ||
                startIndex >= prevTextContents.length ||
                endIndex < 0 ||
                endIndex >= prevTextContents.length ||
                startIndex > endIndex
            )
                return;

            if (isEnd) {
                nextTextContents = prevTextContents
                    .map((item, idx) => {
                        if (idx < startIndex) return item;
                        if (idx === startIndex) {
                            return { ...item, text: item.text.slice(0, startOffset) + text };
                        }
                        return null;
                    })
                    .filter(Boolean) as ITextContent[];
                nextText = nextTextContents.map((item) => item.text).join('');

                const lastTextContent = nextTextContents[nextTextContents.length - 1];
                nextEditingRange = {
                    ...nextEditingRange,
                    startIndex: nextTextContents.length - 1,
                    endIndex: nextTextContents.length - 1,
                    startOffset: lastTextContent.text.length,
                    endOffset: lastTextContent.text.length,
                };
            } else {
                nextTextContents = prevTextContents
                    .map((item, idx) => {
                        if (idx < startIndex) return item;
                        if (idx === startIndex) {
                            return { ...item, text: item.text.slice(0, startOffset) + text };
                        }
                        if (idx < endIndex) return null;
                        if (idx === endIndex) {
                            return { ...item, text: item.text.slice(endOffset) };
                        }
                        return item;
                    })
                    .filter(Boolean) as ITextContent[];
                nextText = nextTextContents.map((item) => item.text).join('');

                const newTextContent = nextTextContents[startIndex];
                nextEditingRange = {
                    ...nextEditingRange,
                    startIndex: startIndex,
                    endIndex: startIndex,
                    startOffset: newTextContent.text.length,
                    endOffset: newTextContent.text.length,
                };
            }
        }

        // FIXME: 这行代码在这里有点突兀，看看是否可以使用 hook 传入执行
        // 通知 TextEditArea 开启手动更新
        this.framework.events.emit(TEXT_EDIT_EVENTS.ALLOW_UPDATE_MANUAL);

        const nextLayerData: ITextLayerProps = { ...prevLayerData, text: nextText, textContents: nextTextContents };
        let nextLayer: ILayerProps = { ...layer, data: nextLayerData };
        nextLayer = this.calcLayout(layer, nextLayer);

        const prevLayer = cloneDeep(layer);

        this.framework.stage.addOrUpdate(nextLayer);
        this.framework.operate.setActiveLayer(nextLayer.id);
        this.framework.history.write(omit(prevLayer, '__isOnTextEditing'), omit(nextLayer, '__isOnTextEditing'));

        // 这里只需要更新选区状态即可，可以不用手动设置选区
        // 因为已通过 framework.events.emit(TEXT_EDIT_EVENTS.ALLOW_UPDATE_MANUAL) 消息
        // 告知 TextEditArea 组件分段内容更新，允许手动更新组件（TextEditArea 组件中 useDeepCompareEffect 内相关逻辑）
        // 在该逻辑内，由于分段文本发生变化，即会触发手动更新，从而更新选区
        getTextEditState(this.framework.identity).setEditingRange(nextEditingRange);
    }

    public static async copyOrCut(layer: ILayerProps, editingRange: EditingRange) {
        if (!this.framework) {
            this.warnNotInitialized();
            return;
        }

        let targetLayer: ILayerProps = { ...cloneDeep(layer), id: uuid(), __isOnTextEditing: false };
        let layerData = targetLayer.data as ITextLayerProps;

        const { textContents = [], text } = layerData;

        const { startIndex, endIndex, startOffset, endOffset } = editingRange;

        // step 1: 根据选区信息，确认文本内容（text、textContents）

        // case 1: startIndex === endIndex，即选中的是同一个分段内的文本
        // 此时新的文本图层不需要分段文本
        if (startIndex === endIndex) {
            let nextText = '';
            let textContent: ITextContent | null = null;

            if (textContents.length > 0) {
                // --- 校验 startIndex 是否在有效范围内 ---
                if (startIndex < 0 || startIndex >= textContents.length) return;

                textContent = textContents[startIndex];
                if (!textContent) return;

                nextText = textContent.text.slice(startOffset, endOffset);
            } else {
                nextText = text.slice(startOffset, endOffset);
            }

            layerData = { ...layerData, text: nextText, textContents: [] };

            // 使用 textContent 的信息，对 layerData 信息进行覆盖
            if (textContent) {
                layerData = {
                    ...layerData,
                    color: textContent.color,
                    fontSize: textContent.fontSize,
                    fontFamily: textContent.fontFamily,
                    bold: textContent.bold,
                    italic: textContent.italic,
                    lineThrough: textContent.lineThrough,
                    underline: textContent.underline,
                };

                if (!layerData.textMark && !!textContent.textMark) {
                    layerData = { ...layerData, textMark: textContent.textMark };
                }
            }
        } else {
            // case 2: startIndex !== endIndex，跨分段选中文本

            // --- 校验 startIndex 和 endIndex 是否在有效范围内 ---
            if (
                startIndex < 0 ||
                startIndex >= textContents.length ||
                endIndex < 0 ||
                endIndex >= textContents.length ||
                startIndex > endIndex
            )
                return;

            let nextTextContents = textContents.slice(startIndex, endIndex + 1);
            nextTextContents = nextTextContents.map((item, idx) => {
                if (idx === 0) {
                    return { ...item, text: item.text.slice(startOffset) };
                }
                if (idx === nextTextContents.length - 1) {
                    return { ...item, text: item.text.slice(0, endOffset) };
                }

                return item;
            });
            const nextText = nextTextContents.map((item) => item.text).join('');

            layerData = { ...layerData, text: nextText, textContents: nextTextContents };
        }

        if (layerData.text === '') return;

        // step 2. 后处理

        // --- 后处理 - layerData ---
        // 移除不必要的属性，如列表文字、变形文字
        Reflect.set(layerData, 'listStyle', '');
        Reflect.deleteProperty(layerData, 'listStyleMaterial');
        Reflect.deleteProperty(layerData, 'shapeExtension');
        targetLayer = { ...targetLayer, id: uuid(), data: layerData };

        // --- 后处理 - materialIds ---
        // 移除列表文字素材 id 记录、变形文字素材 id 记录
        // 重新整理字体素材 id 记录
        // 重新整理划重点素材 id 记录
        const materialIds = cloneDeep(targetLayer.materialIds || {});
        Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_LIST);
        Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_TRANSFORM);
        Reflect.set(materialIds, MaterialIdsType.FONT_FAMILY, getLayerFontFamilyIds(this.framework, targetLayer));

        const textMarkMaterialIds: string[] = [];
        if (layerData.textContents && layerData.textContents.length > 0) {
            layerData.textContents.forEach(({ textMark }) => {
                if (!textMark) return;
                if (!textMarkMaterialIds.includes(textMark.materialId)) {
                    textMarkMaterialIds.push(textMark.materialId);
                }
            });
        } else {
            if (layerData.textMark) textMarkMaterialIds.push(layerData.textMark.materialId);
        }

        if (textMarkMaterialIds.length > 0) {
            Reflect.set(materialIds, MaterialIdsType.TEXT_MARK, textMarkMaterialIds);
        } else {
            Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_MARK);
        }

        targetLayer = { ...targetLayer, materialIds };

        // --- 后处理 - 重新计算文本图层尺寸 ---
        const textSize = TextMeasurer.measure(targetLayer, {
            keepConstantWidth: false,
            keepConstantHeight: false,
        });

        targetLayer = { ...targetLayer, width: textSize.width, height: textSize.height };

        // step 3. 写入剪贴板
        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageExtraInfo = this.framework.getExtraInfo(stageIdx);

        this.framework.getInstance(TextEditDependency).setClipboardData(targetLayer, stageExtraInfo);
    }
}
